import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useMobile } from '../components/Layout';
import { useTheme } from '../ThemeContext';
import { Search, RefreshCw, ChevronDown, ChevronUp, ShieldAlert, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const RISK_META = {
  HIGH:   { bg: '#FEF2F2', text: '#991B1B', border: '#FECACA', label: 'High Risk' },
  MEDIUM: { bg: '#FFFBEB', text: '#92400E', border: '#FDE68A', label: 'Medium Risk' },
  LOW:    { bg: '#F8FAFC', text: '#166534', border: '#DCFCE7', label: 'Low Risk' },
};

const ACTION_META = {
  ESCALATE_TO_MLRO:       { bg: '#FEF2F2', text: '#991B1B', border: '#FECACA', label: 'Escalate to MLRO' },
  ENHANCED_DUE_DILIGENCE: { bg: '#FFFBEB', text: '#92400E', border: '#FDE68A', label: 'Enhanced Due Diligence' },
  PROCEED:                { bg: '#F8FAFC', text: '#166534', border: '#DCFCE7', label: 'Proceed' },
};

const Badge = ({ children, meta, theme }) => (
  <span style={{
    background: meta?.bg || theme.bgInput,
    color: meta?.text || theme.textSecondary,
    border: `1px solid ${meta?.border || theme.border}`,
    padding: '3px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  }}>
    {children || '-'}
  </span>
);

const RiskBadge = ({ risk, theme }) => {
  const m = RISK_META[risk];
  if (!m) return <Badge theme={theme}>Not analysed</Badge>;
  return <Badge theme={theme} meta={m}>{m.label}</Badge>;
};

const ActionBadge = ({ action, theme }) => {
  const m = ACTION_META[action];
  if (!m) return <Badge theme={theme}>No action</Badge>;
  return <Badge theme={theme} meta={m}>{m.label}</Badge>;
};

const AML = () => {
  const navigate = useNavigate();
  const isMobile = useMobile();
  const { theme } = useTheme();

  const [investors, setInvestors] = useState([]);
  const [amlData, setAmlData] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [rerunning, setRerunning] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const invRes = await axios.get(`${API_URL}/investors`, { headers });
      const invList = invRes.data || [];
      setInvestors(invList);

      const results = await Promise.allSettled(
        invList.map(inv =>
          axios.get(`${API_URL}/aml/${inv.id}`, { headers })
            .then(r => ({ id: inv.id, data: r.data }))
            .catch(() => ({ id: inv.id, data: null }))
        )
      );

      const map = {};
      results.forEach(r => {
        if (r.status === 'fulfilled' && r.value.data?.overall_aml_risk) {
          map[r.value.id] = r.value.data;
        }
      });
      setAmlData(map);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const rerunAnalysis = async (investorId, e) => {
    e.stopPropagation();
    setRerunning(investorId);
    try {
      const res = await axios.post(`${API_URL}/aml/analyse/${investorId}`, {}, { headers });
      setAmlData(prev => ({ ...prev, [investorId]: res.data }));
    } catch (err) {
      console.error(err);
    } finally {
      setRerunning(null);
    }
  };

  const analysed = investors.filter(i => amlData[i.id]?.overall_aml_risk);
  const highCount = analysed.filter(i => amlData[i.id]?.overall_aml_risk === 'HIGH').length;
  const medCount = analysed.filter(i => amlData[i.id]?.overall_aml_risk === 'MEDIUM').length;
  const lowCount = analysed.filter(i => amlData[i.id]?.overall_aml_risk === 'LOW').length;
  const eddCount = analysed.filter(i => amlData[i.id]?.recommended_action !== 'PROCEED').length;

  const filtered = investors.filter(inv => {
    const aml = amlData[inv.id];

    if (filter !== 'ALL') {
      if (filter === 'NO_ANALYSIS' && aml?.overall_aml_risk) return false;
      if (filter !== 'NO_ANALYSIS' && aml?.overall_aml_risk !== filter) return false;
    }

    if (search) {
      const q = search.toLowerCase();
      return (
        (inv.full_name || '').toLowerCase().includes(q) ||
        (inv.email || '').toLowerCase().includes(q) ||
        (inv.pseudo_id || '').toLowerCase().includes(q)
      );
    }

    return true;
  });

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: `1px solid ${theme.border}`,
    fontSize: 13,
    color: theme.textPrimary,
    background: theme.bgInput,
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  const primaryBtn = {
    background: '#2563EB',
    color: 'white',
    border: '1px solid #2563EB',
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };

  const secondaryBtn = {
    background: theme.bgInput,
    color: theme.textSecondary,
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };

  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <p style={{ color: theme.textTertiary, fontSize: 14 }}>Loading AML data...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        marginBottom: 24,
        flexWrap: 'wrap',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: theme.textPrimary }}>
            AML Risk Monitor
          </h1>
          <p style={{ margin: '5px 0 0', fontSize: 13, color: theme.textSecondary }}>
            FATF Recommendation 10 and CSSF AML/CFT risk monitoring.
          </p>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 10px',
          border: `1px solid ${highCount > 0 ? '#FECACA' : theme.border}`,
          borderRadius: 8,
          background: theme.bgCard,
          boxShadow: theme.shadowSm,
        }}>
          <ShieldAlert size={15} color={highCount > 0 ? '#991B1B' : theme.textSecondary} />
          <span style={{
            fontSize: 12,
            fontWeight: 600,
            color: highCount > 0 ? '#991B1B' : theme.textSecondary,
          }}>
            {highCount > 0 ? `${highCount} high risk` : 'No high-risk alerts'}
          </span>
        </div>
      </div>

      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        boxShadow: theme.shadowSm,
        marginBottom: 16,
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
          borderBottom: `1px solid ${theme.border}`,
        }}>
          {[
            ['High Risk', highCount, 'HIGH'],
            ['Medium Risk', medCount, 'MEDIUM'],
            ['Low Risk', lowCount, 'LOW'],
            ['EDD / MLRO', eddCount, 'EDD'],
          ].map(([label, value, key], idx) => (
            <button
              key={label}
              onClick={() => setFilter(key === 'EDD' ? 'ALL' : filter === key ? 'ALL' : key)}
              style={{
                textAlign: 'left',
                padding: '16px 18px',
                border: 'none',
                borderRight: !isMobile && idx < 3 ? `1px solid ${theme.border}` : 'none',
                background: filter === key ? theme.bgInput : 'transparent',
                cursor: key === 'EDD' ? 'default' : 'pointer',
              }}
            >
              <p style={{ margin: 0, fontSize: 12, color: theme.textTertiary, fontWeight: 600 }}>{label}</p>
              <p style={{
                margin: '6px 0 0',
                fontSize: 24,
                fontWeight: 700,
                color: key === 'HIGH' && value > 0 ? '#991B1B' : key === 'MEDIUM' && value > 0 ? '#92400E' : theme.textPrimary,
                lineHeight: 1,
              }}>
                {value}
              </p>
            </button>
          ))}
        </div>

        <div style={{
          padding: '14px 16px',
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
            <Search size={14} color={theme.textTertiary} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              placeholder="Search investors, email, pseudo ID"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ ...inputStyle, paddingLeft: 34 }}
            />
          </div>

          <div style={{ display: 'flex', gap: 6, overflowX: 'auto' }}>
            {[
              ['ALL', 'All'],
              ['HIGH', 'High'],
              ['MEDIUM', 'Medium'],
              ['LOW', 'Low'],
              ['NO_ANALYSIS', 'Not analysed'],
            ].map(([key, label]) => {
              const active = filter === key;
              return (
                <button key={key} onClick={() => setFilter(key)} style={{
                  padding: '7px 10px',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: `1px solid ${active ? '#CBD5E1' : theme.border}`,
                  background: active ? '#F8FAFC' : theme.bgInput,
                  color: active ? theme.textPrimary : theme.textSecondary,
                  whiteSpace: 'nowrap',
                }}>
                  {label}
                </button>
              );
            })}
          </div>

          <span style={{ marginLeft: 'auto', fontSize: 12, color: theme.textTertiary, whiteSpace: 'nowrap' }}>
            {filtered.length} of {investors.length}
          </span>
        </div>
      </div>

      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        boxShadow: theme.shadowSm,
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 16px',
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
        }}>
          <div>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: theme.textPrimary }}>
              AML Register
            </p>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: theme.textTertiary }}>
              Investor risk ratings, actions, red flags, and CSSF narrative.
            </p>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: theme.textPrimary }}>No investors found</p>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.textSecondary }}>Try a different search or risk filter.</p>
          </div>
        ) : (
          <div>
            {filtered.map((inv, idx) => {
              const aml = amlData[inv.id];
              const expanded = expandedId === inv.id;
              const initials = (inv.full_name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

              return (
                <div key={inv.id} style={{ borderBottom: idx < filtered.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
                  <div
                    onClick={() => setExpandedId(expanded ? null : inv.id)}
                    style={{
                      padding: isMobile ? 14 : '14px 16px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      background: expanded ? theme.bgInput : 'transparent',
                    }}
                  >
                    <div style={{
                      width: 34,
                      height: 34,
                      borderRadius: 8,
                      background: theme.bgInput,
                      border: `1px solid ${theme.border}`,
                      color: theme.textSecondary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 600,
                      flexShrink: 0,
                    }}>
                      {initials}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: theme.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {inv.full_name || '-'}
                      </p>
                      <p style={{ margin: '2px 0 0', fontSize: 11, color: theme.textTertiary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {inv.email || inv.pseudo_id || '-'}
                      </p>
                    </div>

                    {!isMobile && <RiskBadge risk={aml?.overall_aml_risk} theme={theme} />}
                    {!isMobile && aml?.recommended_action && <ActionBadge action={aml.recommended_action} theme={theme} />}

                    {!isMobile && aml?.rated_at && (
                      <span style={{ fontSize: 11, color: theme.textTertiary, flexShrink: 0 }}>
                        {new Date(aml.rated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    )}

                    <button onClick={e => rerunAnalysis(inv.id, e)} disabled={rerunning === inv.id} style={{
                      ...secondaryBtn,
                      cursor: rerunning === inv.id ? 'not-allowed' : 'pointer',
                    }}>
                      <RefreshCw size={11} style={{ animation: rerunning === inv.id ? 'spin 1s linear infinite' : 'none' }} />
                      {rerunning === inv.id ? 'Running' : aml ? 'Re-run' : 'Analyse'}
                    </button>

                    {expanded ? <ChevronUp size={15} color={theme.textTertiary} /> : <ChevronDown size={15} color={theme.textTertiary} />}
                  </div>

                  {isMobile && (
                    <div style={{ padding: '0 14px 12px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <RiskBadge risk={aml?.overall_aml_risk} theme={theme} />
                      {aml?.recommended_action && <ActionBadge action={aml.recommended_action} theme={theme} />}
                    </div>
                  )}

                  {expanded && aml && (
                    <div style={{ padding: isMobile ? '16px 14px' : '18px 20px', background: theme.bgBase, borderTop: `1px solid ${theme.border}` }}>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20, marginBottom: 16 }}>
                        <div>
                          <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: theme.textPrimary }}>
                            Risk Dimensions
                          </p>
                          {[
                            ['Geographic Risk', aml.geographic_risk],
                            ['Behavioural Risk', aml.behavioural_score],
                            ['Amount Risk', aml.amount_risk],
                          ].map(([label, value]) => (
                            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: `1px solid ${theme.border}` }}>
                              <span style={{ fontSize: 13, color: theme.textSecondary }}>{label}</span>
                              <RiskBadge risk={value} theme={theme} />
                            </div>
                          ))}

                          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                            {aml.pep_indicators && <Badge theme={theme} meta={RISK_META.HIGH}>PEP indicators</Badge>}
                            {aml.structuring_detected && <Badge theme={theme} meta={RISK_META.HIGH}>Structuring detected</Badge>}
                          </div>
                        </div>

                        <div>
                          <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: theme.textPrimary }}>
                            Red Flags ({aml.red_flags?.length || 0})
                          </p>
                          {aml.red_flags?.length > 0 ? aml.red_flags.map((flag, i) => (
                            <div key={`flag-${i}`} style={{
                              background: theme.bgCard,
                              border: `1px solid ${flag.severity === 'HIGH' ? '#FECACA' : theme.border}`,
                              borderRadius: 8,
                              padding: '10px 12px',
                              marginBottom: 8,
                            }}>
                              <Badge theme={theme} meta={RISK_META[flag.severity]}>
                                {flag.severity} · {flag.category}
                              </Badge>
                              <p style={{ margin: '7px 0 0', fontSize: 12, color: theme.textSecondary, lineHeight: 1.5 }}>
                                {flag.description}
                              </p>
                            </div>
                          )) : (
                            <p style={{ fontSize: 13, color: theme.textSecondary, margin: 0 }}>No red flags detected.</p>
                          )}
                        </div>
                      </div>

                      {aml.positive_indicators?.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                          <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: theme.textPrimary }}>
                            Positive Indicators
                          </p>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {aml.positive_indicators.map((pi, i) => (
                              <Badge key={i} theme={theme}>{pi}</Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {aml.risk_narrative && (
                        <div style={{ marginBottom: 16 }}>
                          <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: theme.textPrimary }}>
                            CSSF Risk Narrative
                          </p>
                          <div style={{
                            background: theme.bgCard,
                            border: `1px solid ${theme.border}`,
                            borderRadius: 8,
                            padding: '12px 14px',
                            fontSize: 13,
                            color: theme.textSecondary,
                            lineHeight: 1.7,
                          }}>
                            {aml.risk_narrative}
                          </div>
                        </div>
                      )}

                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button onClick={() => navigate(`/investors/${inv.id}`)} style={primaryBtn}>
                          View Investor Profile <ArrowRight size={13} />
                        </button>
                      </div>
                    </div>
                  )}

                  {expanded && !aml && (
                    <div style={{ padding: 18, background: theme.bgBase, borderTop: `1px solid ${theme.border}` }}>
                      <p style={{ margin: '0 0 12px', fontSize: 13, color: theme.textSecondary }}>
                        No AML analysis exists for this investor yet.
                      </p>
                      <button onClick={e => rerunAnalysis(inv.id, e)} disabled={rerunning === inv.id} style={primaryBtn}>
                        <RefreshCw size={13} /> {rerunning === inv.id ? 'Running analysis' : 'Run AML Analysis'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AML;
